import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header/Header';
import PnLSummaryCards from '../components/PnLSummaryCards/PnLSummaryCards';
import WalletPositionsTable from '../components/WalletPositionsTable/WalletPositionsTable';
import { useWalletData } from '../hooks/useWalletData';
import './WalletProfilePage.css';

/**
 * Shorten wallet address for display
 */
const shortenAddress = (address) => {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 8)}...${address.slice(-6)}`;
};

/**
 * Wallet Profile Page
 * Shows detailed analytics for a specific Hyperliquid wallet
 * Following grey-black theme
 */
function WalletProfilePage() {
  const { address } = useParams();
  const navigate = useNavigate();

  const {
    loading,
    error,
    walletData,
    positions,
    pnlHistory,
    refresh
  } = useWalletData(address);

  // Copy address to clipboard
  const copyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      // Simple feedback (можно улучшить с toast notification)
      alert('Address copied to clipboard!');
    }
  };

  return (
    <div className="wallet-profile-page">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="container-fluid">
        {/* Back Button */}
        <div className="back-navigation mb-3">
          <Link to="/hyperliquid" className="back-link">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            Back to Positions
          </Link>
        </div>

        {/* Wallet Header */}
        <div className="wallet-header mb-4">
          <div className="wallet-info">
            <h1 className="wallet-title">Wallet Profile</h1>
            <div className="wallet-address-container">
              <span className="wallet-address" title={address}>{shortenAddress(address)}</span>
              <button className="copy-btn" onClick={copyAddress} title="Copy full address">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
              </button>
              <button className="refresh-btn" onClick={refresh} title="Refresh data">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        {/* PnL Summary Cards */}
        <PnLSummaryCards walletData={walletData} loading={loading} />

        {/* Active Positions */}
        <WalletPositionsTable positions={positions} loading={loading} />

        {/* PnL History Section */}
        <div className="card pnl-history-card">
          <div className="card-header">
            <h6 className="mb-0">PnL History</h6>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-light" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : pnlHistory && pnlHistory.length > 0 ? (
              <div className="pnl-history-list">
                {pnlHistory
                  .filter(entry => entry.pnlTotal !== 0) // Filter out zero-value records
                  .slice(0, 10)
                  .map((entry, index) => (
                  <div key={index} className="pnl-history-entry">
                    <span className="entry-date">
                      {new Date(entry.timestamp).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <span className={`entry-value ${entry.pnlTotal >= 0 ? 'positive' : 'negative'}`}>
                      {entry.pnlTotal >= 0 ? '+' : ''}${Math.abs(entry.pnlTotal).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted py-5">No PnL history available</p>
            )}
          </div>
        </div>

        {/* Footer Note */}
        <div className="wallet-footer-note mt-4 mb-5">
          <p className="text-muted small text-center">
            Data is updated every 10 seconds. Click refresh button for immediate update.
          </p>
        </div>
      </div>
    </div>
  );
}

export default WalletProfilePage;
